/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    this.updateCustomElements = this.updateCustomElements.bind(this);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
      this.updateCustomElements();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }

  /**
   * Update custom free shipping progress bar and checkout button
   */
  updateCustomElements() {
    // Get cart data from the form
    const cartForm = this.querySelector('cart-form');
    if (!cartForm) return;

    // Get cart total from the existing subtotal element
    const subtotalElement = this.querySelector('[data-wpd-cart-total]');
    if (!subtotalElement) return;

    const cartTotalText = subtotalElement.textContent.trim();
    const cartTotalMatch = cartTotalText.match(/[\d,]+\.?\d*/);
    if (!cartTotalMatch) return;

    const cartTotal = parseFloat(cartTotalMatch[0].replace(/,/g, '')) * 100; // Convert to cents
    const freeShippingThreshold = 20000; // $200 in cents
    
    // Update free shipping progress bar
    this.updateFreeShippingProgress(cartTotal, freeShippingThreshold);
    
    // Update checkout button
    this.updateCheckoutButton(cartTotalText);
  }

  /**
   * Update free shipping progress bar
   */
  updateFreeShippingProgress(cartTotal, threshold) {
    const progressContainer = this.querySelector('.cart-drawer-free-shipping-progress');
    if (!progressContainer) return;

    const remainingAmount = Math.max(0, threshold - cartTotal);
    const progressPercentage = Math.min(100, (cartTotal / threshold) * 100);

    // Update status message
    const statusElement = progressContainer.querySelector('.cart-drawer-free-shipping-progress__status');
    if (statusElement) {
      if (cartTotal >= threshold) {
        statusElement.textContent = "You've unlocked Free Shipping!";
        statusElement.classList.add('cart-drawer-free-shipping-progress__status--success');
        progressContainer.classList.add('cart-drawer-free-shipping-progress--complete');
      } else {
        const remainingFormatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(remainingAmount / 100);
        statusElement.textContent = `Add ${remainingFormatted} more to get Free Shipping`;
        statusElement.classList.remove('cart-drawer-free-shipping-progress__status--success');
        progressContainer.classList.remove('cart-drawer-free-shipping-progress--complete');
      }
    }

    // Update progress bar fill
    const fillElement = progressContainer.querySelector('.cart-drawer-free-shipping-progress__fill');
    if (fillElement) {
      fillElement.style.width = progressPercentage + '%';
      if (cartTotal >= threshold) {
        fillElement.classList.add('cart-drawer-free-shipping-progress__fill--complete');
      } else {
        fillElement.classList.remove('cart-drawer-free-shipping-progress__fill--complete');
      }
    }
  }

  /**
   * Update checkout button with current total
   */
  updateCheckoutButton(cartTotalText) {
    const checkoutButtons = this.querySelectorAll('.checkout-buttons a');
    checkoutButtons.forEach(button => {
      const checkoutText = button.textContent.split('|')[0].trim();
      button.textContent = `${checkoutText} | ${cartTotalText}`;
    });
  }
}

window.customElements.define('cart-drawer', CartDrawer);
